// src/config.js
// ============================================================================
// Role, Module, and Permission configuration for PMS/HMS SaaS application
// ============================================================================

// ---------------------------------------------------------------------------
// ROLES — Numeric IDs for sorting, storage, and API communication
// ---------------------------------------------------------------------------
export const ROLES = {
  ADMINISTRATOR: 1,
  FINANCE_MANAGER: 2,
  HOUSEKEEPING_SUPERVISOR: 3,
  PROPERTY_MANAGER: 4,
  FRONT_DESK_MANAGER: 5,
  FRONT_DESK_AGENT: 6,
};

// Human-readable labels for each role (keyed by role ID)
export const ROLE_LABELS = {
  [ROLES.ADMINISTRATOR]: "Administrator",
  [ROLES.FINANCE_MANAGER]: "Finance Manager",
  [ROLES.HOUSEKEEPING_SUPERVISOR]: "Housekeeping Supervisor",
  [ROLES.PROPERTY_MANAGER]: "Property Manager",
  [ROLES.FRONT_DESK_MANAGER]: "Front Desk Manager",
  [ROLES.FRONT_DESK_AGENT]: "Front Desk Agent",
};

// ---------------------------------------------------------------------------
// MODULES — Slug values used in API payloads and permission storage
// ---------------------------------------------------------------------------
export const MODULES = {
  HMS: "hms",
  POS: "pos",
  OFFICE: "ns-office",
  FEEDBACK: "feedback",
  DIGITALMENU: "digital-menu",
  BOOKINGENGINE: "booking-engine",
};

// Human-readable labels for each module (keyed by module slug)
export const MODULE_LABELS = {
  [MODULES.HMS]: "HMS",
  [MODULES.POS]: "POS",
  [MODULES.OFFICE]: "NS Office",
  [MODULES.FEEDBACK]: "Feedback",
  [MODULES.DIGITALMENU]: "Digital Menu",
  [MODULES.BOOKINGENGINE]: "Booking Engine",
};

// ---------------------------------------------------------------------------
// ROLE_PERMISSIONS — Default module access per role (overridable per user)
// ---------------------------------------------------------------------------
export const ROLE_PERMISSIONS = {
  [ROLES.ADMINISTRATOR]: [
    MODULES.HMS,
    MODULES.POS,
    MODULES.OFFICE,
    MODULES.FEEDBACK,
    MODULES.DIGITALMENU,
    MODULES.BOOKINGENGINE,
  ],
  [ROLES.FINANCE_MANAGER]: [MODULES.HMS, MODULES.POS],
  [ROLES.HOUSEKEEPING_SUPERVISOR]: [MODULES.HMS],
  [ROLES.PROPERTY_MANAGER]: [MODULES.HMS, MODULES.POS, MODULES.OFFICE],
  [ROLES.FRONT_DESK_MANAGER]: [MODULES.HMS],
  [ROLES.FRONT_DESK_AGENT]: [MODULES.HMS],
};

// ---------------------------------------------------------------------------
// Utility helpers
// ---------------------------------------------------------------------------

/**
 * Get human-readable role name from a role ID.
 * Falls back to "Unknown Role" for invalid IDs.
 */
export const getRoleName = (roleId) => {
  return ROLE_LABELS[roleId] || "Unknown Role";
};

/**
 * Get human-readable module label from a module slug.
 * Falls back to the slug itself if no label is defined.
 */
export const getModuleLabel = (moduleSlug) => {
  return MODULE_LABELS[moduleSlug] || moduleSlug;
};

/**
 * Build react-select options array for roles.
 * Returns [{ value: 1, label: "Administrator" }, ...]
 */
export const getRoleOptions = () => {
  return Object.values(ROLES).map((id) => ({
    value: id,
    label: getRoleName(id),
  }));
};

/**
 * Build react-select options array for modules.
 * Returns [{ value: "hms", label: "HMS" }, ...]
 */
export const getModuleOptions = () => {
  return Object.values(MODULES).map((slug) => ({
    value: slug,
    label: getModuleLabel(slug),
  }));
};

/**
 * Initialize app_access in localStorage on app startup.
 * Stores available module slugs; can be customized per user role or company.
 */
export const initializeAppAccess = () => {
  if (!localStorage.getItem("app_access")) {
    localStorage.setItem("app_access", JSON.stringify(Object.values(MODULES)));
    console.log("Initialized app_access in localStorage:", Object.values(MODULES));
  }
};